/** The activity record's binding of the records core (plan §3, §6, §7, §8, §10.3): what the
 *  page's `DraftProvider` and `RecordEditorProvider` need, in view mode and in edit mode.
 *
 *  - **Baselines** — the editable fields' stored values in the changeset's wire form. They
 *    come from edit_context (`baselines`); until it arrives, the same values read from the
 *    detail (`activityBaselines`, the server's readers' form), so the cards render at once.
 *  - **Fields** — the server's edit catalog (`catalogRecordField`: labels, kinds, inputs,
 *    choices, what a feed does), else the registry, which agrees with it (the contract test).
 *  - **Verdicts (D4)** — edit_context `feed.verdicts`: what the next sync of the feed that
 *    imports the activity does to an edit of each field (a changed field reads the verdict
 *    the PREVIEW gives its new value). The feed names the record's "managed by" only while
 *    its source may update activities — a create-only source keeps every edit.
 *  - **Editability** — the `ConsoleCatalogEditEnabled` kill switch ("Editing is switched
 *    off"), `console_activities_manage`, an owning operator, each field's permissions and
 *    guards.
 *  - **Preview / apply** — the activity changeset's (`useActivityChangesetPreview`,
 *    `useApplyActivityChangeset`).
 *  - **The re-key question (O11)** — the catalog's `rekey_import_matching`, raised by a
 *    Custom ID change on an activity an import has touched: asked under the field and in
 *    the ledger in the server's words ("Re-key import matching to FH-2? FareHarbor's next
 *    sync will create a new record."); Save waits for it. **Re-key** is the answer
 *    (`options.rekey_import_matching`, re-previewed), bound to the Custom ID it was given
 *    for and sent with it (`rekey_import_matching_to`, the catalog's `value` option that
 *    `confirms` it, normalized as the server compares — `normalizeCustomId`); any later
 *    change of the Custom ID asks again. **Cancel** takes the Custom ID change back
 *    (`activityOptions` — the catalog records' `rekeyQuestion`). Clearing a Custom ID while
 *    the import identity is set is the server's refusal, under the field.
 *  - **Names** — the picked records the detail names (category, country, currency, access
 *    tags); the pickers add what the operator picks. */
import { useMemo } from 'react';

import type { ActivityDetail } from '@/api/activities';
import {
  useActivityChangesetPreview,
  useApplyActivityChangeset,
  type ActivityRecordCatalog,
  type ActivityRecordEditableFieldKey,
  type ActivityRecordEditContext,
} from '@/api/activityRecord';
import { CATALOG_EDITING_OFF, CATALOG_NO_OPERATOR } from '@/features/catalog/editGate';
import { normalizeCustomId } from '@/features/catalog/identity';
import { catalogRecordField } from '@/features/catalog/recordFields';
import { rekeyAskedUpFront, rekeyQuestionOptions } from '@/features/catalog/rekeyQuestion';
import { recordKey, type RecordChangeset, type RecordKey } from '@/features/records/draft/changeSet';
import type {
  RecordEditability,
  RecordEditorConfig,
  RecordField,
  RecordOption,
  RecordOptionRef,
  RecordPreviewQuery,
  RecordVerdict,
} from '@/features/records/editor/types';

import { activityTitle } from './parts/activityNames';
import { ACTIVITY_ENTITY, activityRecordFields } from './registry';
import { currencyLabel, useActivityWidgets } from './widgets';

export const ACTIVITY_MANAGE_PERM = 'console_activities_manage';

/** The record's own times (a receipt, "changed on the server at …", a restored draft) read in
 *  UTC, labelled — as its Identity card's created / updated do. An activity has no wall-clock
 *  field of its own. */
export const ACTIVITY_TIMEZONE = 'UTC';

/** The record key of the activity (`activity:<id>`). */
export const activityRec = (id: number): RecordKey => recordKey(ACTIVITY_ENTITY, id);

const num = (v: number | null | undefined) => (typeof v === 'number' ? v : null);

/** A pair the server stores as two columns: null when neither is set. */
function pair<T extends Record<string, number | null>>(v: T): T | null {
  return Object.values(v).every((x) => x === null) ? null : v;
}

/** The editable fields' stored values in the wire form of their changes — the form the
 *  server's readers give edit_context (ids for the picked records, `{latitude, longitude}`
 *  and `{min, max}` hours or null, the opening hours as stored, the nearby activities in
 *  order, the access tags' ids ascending, the custom ID by the identity rule: stripped,
 *  null when blank). */
export function activityBaselineValues(a: ActivityDetail): Record<ActivityRecordEditableFieldKey, unknown> {
  return {
    name: a.name,
    display_name: a.display_name,
    type: a.type,
    category: a.category?.id ?? null,
    preview_text: a.preview_text,
    description: a.description,
    address: a.address,
    coordinates: pair({ latitude: num(a.latitude), longitude: num(a.longitude) }),
    country: a.country?.id ?? null,
    directions_type: a.directions_type,
    directions_url: a.directions_url,
    duration: pair({ min: num(a.min_duration), max: num(a.max_duration) }),
    opening_hours: a.opening_hours ?? null,
    price_type: a.price_type,
    payment_type: a.payment_type,
    cost_min: a.cost_min,
    cost_max: a.cost_max,
    currency: a.currency?.id ?? null,
    cost_prefix: a.cost_prefix,
    cost_unit: a.cost_unit,
    price_level: a.price_level,
    website: a.website,
    phone: a.phone,
    email: a.email,
    ext_link: a.ext_link,
    children: a.children.map((c) => c.id),
    atag: a.atag.map((t) => t.id).sort((x, y) => x - y),
    custom_id: normalizeCustomId(a.custom_id),
    internal_notes: a.internal_notes,
  };
}

/** The draft's baselines from the detail (`"activity:<id>"` → field → value). */
export function activityBaselines(a: ActivityDetail): Record<RecordKey, Record<string, unknown>> {
  return { [activityRec(a.id)]: activityBaselineValues(a) };
}

/** The picked records the detail names, by field: shown by name in both modes. */
export function activityOptionRefs(a: ActivityDetail): Record<string, RecordOptionRef[]> {
  return {
    category: a.category ? [{ id: a.category.id, label: a.category.title || `Category #${a.category.id}` }] : [],
    country: a.country ? [{ id: a.country.id, label: a.country.name }] : [],
    currency: a.currency ? [{ id: a.currency.id, label: currencyLabel(a.currency) }] : [],
    atag: a.atag.map((t) => ({ id: t.id, label: t.name ?? `Access tag #${t.id}`, group: 'Access tags' })),
  };
}

/** Where a read-only value comes from — its tooltip. */
const PROVENANCE: Readonly<Record<string, string>> = {
  place: 'From Google: the place the activity is linked to',
  ratings: 'From Google: the stored aggregate over every rating source',
  place_data: 'From Google: the place details stored on the activity',
  timezone: 'From the coordinates, recomputed on save',
  nearby_of: 'Activities listing this one among their nearby activities',
  source: 'The newest import that touched the activity',
  ext_id: 'The import identity, derived from the custom ID',
  version: 'The app cache version',
  notes: 'Legacy: not shipped to the app, edited nowhere',
  ontrip_description: 'Legacy: not shipped to the app, edited nowhere',
  description_abbrev: 'Legacy: not shipped to the app, edited nowhere',
};

/** Why the editor cannot open on this activity, or null — the permission aside (no
 *  permission, no Edit at all). The switch's words win: they say the environment, not the
 *  record. */
export function activityEditBlockedReason({
  detail,
  catalog,
  editContext,
}: {
  detail: Pick<ActivityDetail, 'tenant'>;
  catalog?: Pick<ActivityRecordCatalog, 'enabled'> | null;
  editContext?: Pick<ActivityRecordEditContext, 'flags'> | null;
}): string | null {
  if (catalog?.enabled === false || editContext?.flags.catalog_edit_enabled === false) return CATALOG_EDITING_OFF;
  if (!detail.tenant) return CATALOG_NO_OPERATOR;
  return null;
}

/** The feed that writes the activity — by name — while its source may update activities;
 *  null when no feed does (created in the console, or a create-only source). */
export function activityManagedBy(ctx: Pick<ActivityRecordEditContext, 'feed'>): string | null {
  const feed = ctx.feed.feed;
  return feed?.updates ? feed.source_name : null;
}

/** The catalog's choice questions as the editor asks them: the re-key question
 *  (`rekeyQuestionOptions` — the itinerary's too) in the preview's words, its Cancel taking
 *  the Custom ID change back ("Cancel keeps the Custom ID (remove the change)"); the Custom ID
 *  a Re-key confirms (`rekey_import_matching_to`) in the identity rule's form. */
export function activityOptions(catalog: Pick<ActivityRecordCatalog, 'options'>): RecordOption[] {
  return rekeyQuestionOptions(catalog.options);
}

/** Whether a raised question is asked before the preview says so: the re-key question only on
 *  an activity an import has touched (its newest import run, `source`) — the preview decides. */
export function activityOptionAsked(detail: Pick<ActivityDetail, 'source'>) {
  return rekeyAskedUpFront(detail.source !== null);
}

/** The activity's changeset preview as the editor reads it. */
export function useActivityPreview(id: number, body: RecordChangeset | null): RecordPreviewQuery {
  const q = useActivityChangesetPreview(id, body);
  return {
    data: q.data,
    isFetching: q.isFetching,
    isPlaceholderData: q.isPlaceholderData,
    error: q.error,
    // Asked nothing (no change of the activity in the body): settled, not loading.
    isEnabled: q.isEnabled,
  };
}

export interface ActivityEditorInput {
  detail: ActivityDetail;
  /** The server's edit catalog and the activity's edit context; until both are here, view
   *  mode from the registry and the detail. */
  catalog?: ActivityRecordCatalog | null;
  editContext?: ActivityRecordEditContext | null;
  /** A ledger row for a field that is not on screen (another page tab). */
  onFocusMissing?: (rec: RecordKey, path: string) => void;
}

const REGISTRY_FIELDS = activityRecordFields();
const NONE: never[] = [];

/** What both modes share: the detail's facts. */
function sharedParts(detail: ActivityDetail): Omit<RecordEditorConfig, 'fields' | 'usePreview' | 'useApply' | 'widgets'> {
  return {
    options: NONE,
    actions: NONE,
    effects: NONE,
    timezone: ACTIVITY_TIMEZONE,
    recordNoun: 'activity',
    managedBy: null,
    // Its sources sync: "FareHarbor's next sync will undo this" (plan §7).
    feedEvent: 'sync',
    verdictOf: () => null,
    provenanceOf: (field) => PROVENANCE[field.key],
    optionRefs: activityOptionRefs(detail),
  };
}

/** The editing half (catalog + edit context), pure — for tests. */
export function activityEditorConfigParts({
  detail,
  catalog,
  editContext: ctx,
  onFocusMissing,
}: ActivityEditorInput & {
  catalog: ActivityRecordCatalog;
  editContext: ActivityRecordEditContext;
}): Omit<RecordEditorConfig, 'usePreview' | 'useApply' | 'widgets'> {
  const rec = activityRec(detail.id);
  const managedBy = activityManagedBy(ctx);
  const blocked = activityEditBlockedReason({ detail, catalog, editContext: ctx });

  const verdictOf = (r: RecordKey, field: RecordField): RecordVerdict | null =>
    r === rec ? (ctx.feed.verdicts[field.key] ?? null) : null;

  const editabilityOf = (r: RecordKey, field: RecordField): RecordEditability => {
    if (blocked) return { editable: false, reason: blocked };
    if (!ctx.permissions[ACTIVITY_MANAGE_PERM]) {
      return { editable: false, reason: `Needs the ${ACTIVITY_MANAGE_PERM} permission` };
    }
    const missing = field.permissions.find((p) => !ctx.permissions[p]);
    if (missing) return { editable: false, reason: `Needs the ${missing} permission` };
    const guards = ctx.blocked[`${r}:${field.key}`] ?? [];
    if (guards.length > 0) return { editable: false, reason: guards.map((g) => catalog.guards[g] ?? g).join(' ') };
    return { editable: true };
  };

  return {
    ...sharedParts(detail),
    fields: catalog.fields.map(catalogRecordField),
    options: activityOptions(catalog),
    isOptionAsked: activityOptionAsked(detail),
    effects: catalog.effects,
    managedBy,
    verdictOf,
    editabilityOf,
    groupOf: () => ({ id: ACTIVITY_ENTITY, title: activityTitle(detail) }),
    recordOrder: [rec],
    maxChanges: catalog.max_changes,
    descriptionMaxLength: catalog.description_max_length,
    ...(onFocusMissing ? { onFocusMissing } : {}),
  };
}

/** The activity's `RecordEditorConfig` — stable while its inputs are. */
export function useActivityEditorConfig({ detail, catalog, editContext, onFocusMissing }: ActivityEditorInput): RecordEditorConfig {
  const widgets = useActivityWidgets(detail);
  const id = detail.id;
  return useMemo<RecordEditorConfig>(() => {
    // The same hooks in both halves (React's hook order): until the editor's data is here the
    // page is in view mode, where the editor asks no preview and nothing saves.
    const hooks: Pick<RecordEditorConfig, 'usePreview' | 'useApply'> = {
      usePreview: (body) => useActivityPreview(id, body),
      useApply: (opts) => useApplyActivityChangeset(id, opts),
    };
    if (!catalog || !editContext) {
      return { ...sharedParts(detail), fields: REGISTRY_FIELDS, description: false, widgets, ...hooks };
    }
    return { ...activityEditorConfigParts({ detail, catalog, editContext, onFocusMissing }), widgets, ...hooks };
  }, [detail, catalog, editContext, onFocusMissing, widgets, id]);
}
